// ═══════════════════════════════════════════════════════════════
// share-snapshot — freeze the customer-visible parts of a job onto
// the sharedEstimates / sharedInvoices doc at send time.
//
// Everything here is a SNAPSHOT on purpose. The public pages read
// only from the share doc, never from the live job, so editing a job
// (or an inspection, or a checklist template) after sending can't
// rewrite what the customer already saw and approved.
// ═══════════════════════════════════════════════════════════════
import { getDoc, doc } from 'firebase/firestore';
import { inspectionsCol } from '../database.js';
import { buildInspectionSnapshot } from './inspection-helpers.js';
import { normalizeInspection } from './inspection-helpers.js';
import { normalizeJobTax, computeJobTax, computeGrandTotal } from './job-helpers.js';

// Freeze the tax that applied AT SEND TIME onto the share doc.
//
// This is the whole reason it's a snapshot. Rates change: a county
// raises its rate in April, the mechanic edits the saved preset, and
// every estimate ever sent must keep saying what it said. The public
// page reads only these fields, so the January invoice still shows
// January's rate and January's dollars no matter what happens after.
//
// Returns an empty object for an untaxed job, so the share doc gets
// no tax fields at all and renders exactly as it does today.
export function buildTaxSnapshot(job) {
  const tax = normalizeJobTax(job?.tax);
  if (!tax) return {};
  const source = {
    lineItems: Array.isArray(job?.lineItems) ? job.lineItems : [],
    shopSupplies: Number(job?.shopSupplies) || 0,
    tax,
  };
  const bits = computeJobTax(source);
  if (bits.total <= 0) return {};
  return {
    tax,
    // Stored as computed, not recomputed on the public page. A future
    // change to the rounding rules must not restate a sent invoice.
    taxTotal: bits.total,
    taxLaborAmount: bits.laborTax,
    taxPartsAmount: bits.partsAndSuppliesTax,
    grandTotal: computeGrandTotal(source),
  };
}

export function buildJobPhotosSnapshot(job) {
  const photos = Array.isArray(job?.photos) ? job.photos : [];
  if (photos.length === 0) return null;
  return photos
    .filter((p) => p?.url)
    .map((p) => ({
      url: p.url,
      note: (p.note || '').trim(),
    }));
}

// Read the job's inspection and reduce it to the customer-facing
// report. Returns null when there's no inspection, or when nothing in
// it has been answered yet.
//
// Never throws: a failed read must not block sending an estimate.
// Losing the report is a far smaller problem than the mechanic being
// unable to send the customer a price.
export async function readInspectionSnapshot(jobId) {
  if (!jobId) return null;
  try {
    const snap = await getDoc(doc(inspectionsCol(), jobId));
    if (!snap.exists()) return null;
    return buildInspectionSnapshot(normalizeInspection(snap.data()));
  } catch (e) {
    console.error('Could not read inspection for share doc:', e);
    return null;
  }
}
